<div
  class="relative flex flex-col w-full h-full p-2 sm:p-4 md:p-6 lg:p-8 xl:p-10 2xl:p-12 overflow-hidden bg-dashboard-bg">
  <!-- Header -->
  <div class="relative z-10 flex items-start justify-between mb-3 sm:mb-4 md:mb-6 lg:mb-8">
    <h2
      class="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold text-white tracking-widest uppercase font-tech">
      LBS / HORA — Por Máquina
    </h2>
    <div
      class="text-white/80 font-tech tracking-widest text-xs sm:text-sm md:text-base px-3 py-1 sm:px-4 sm:py-2 rounded-md border border-white/20 uppercase">
      {{ shiftLabel() }}
    </div>
  </div>

  <!-- KPI Row -->
  <div class="relative z-10 grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 lg:gap-6 mb-3 sm:mb-4 md:mb-6 lg:mb-8">
    <div class="bg-white/5 border border-white/10 rounded-lg px-3 py-3 sm:px-4 sm:py-3 flex flex-col gap-1">
      <div class="text-white/60 text-[10px] sm:text-xs tracking-[0.18em] uppercase">Promedio del grupo</div>
      <div class="text-white font-tech text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-none tracking-tight">
        {{ shiftAverage() }}<span class="text-white/45 font-sans text-base sm:text-lg ml-1">Lbs/h</span>
      </div>
      <div class="text-white/40 text-[10px] sm:text-xs">{{ producingMachines().length }} máquinas activas</div>
    </div>

    <div class="bg-white/5 border border-white/10 rounded-lg px-3 py-3 sm:px-4 sm:py-3 flex flex-col gap-1">
      <div class="text-white/60 text-[10px] sm:text-xs tracking-[0.18em] uppercase">Máquina top</div>
      <div class="text-white font-tech text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-none tracking-tight">
        {{ topMachine()?.machineCode ?? '—' }}
        @if (topMachine()) {
        <span class="text-white/45 font-sans text-base sm:text-lg ml-1">{{ topMachine()!.lbsPerHour }} Lbs/h</span>
        }
      </div>
      <div class="text-white/40 text-[10px] sm:text-xs">
        @if (topMachine()?.gauge) { {{ topMachine()!.gauge }}G } @else { — }
      </div>
    </div>

    <div class="bg-white/5 border border-white/10 rounded-lg px-3 py-3 sm:px-4 sm:py-3 flex flex-col gap-1">
      <div class="text-white/60 text-[10px] sm:text-xs tracking-[0.18em] uppercase">Máquina baja</div>
      <div class="text-white font-tech text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-none tracking-tight">
        {{ lowMachine()?.machineCode ?? '—' }}
        @if (lowMachine()) {
        <span class="text-white/45 font-sans text-base sm:text-lg ml-1">{{ lowMachine()!.lbsPerHour }} Lbs/h</span>
        }
      </div>
      <div class="text-white/40 text-[10px] sm:text-xs">
        @if (lowMachine()?.gauge) { {{ lowMachine()!.gauge }}G — revisar } @else { — }
      </div>
    </div>
  </div>

  <!-- Machines Grid -->
  @if (machines().length === 0) {
  <div class="relative z-10 flex-1 flex items-center justify-center text-white/40 font-tech tracking-widest text-base sm:text-lg uppercase">
    Sin datos de producción
  </div>
  } @else {
  <div class="relative z-10 flex-1 grid grid-cols-6 gap-2 sm:gap-3 md:gap-4 min-h-0"
    [@staggerSlideIn]="animationTrigger()">
    @for (tile of tiles(); track tile.assetId) {
    <div
      class="relative bg-white/[0.03] border border-white/[0.07] border-l-[3px] rounded-lg px-3 py-3 sm:px-4 sm:pt-3 sm:pb-4 flex flex-col justify-between min-h-0 overflow-hidden"
      [class.border-l-green-500]="tile.tier === 'top'"
      [class.border-l-amber-500]="tile.tier === 'low'"
      [class.bg-white/[0.015]]="!tile.isActive"
      [class.border-white/[0.05]]="!tile.isActive">
      <div
        class="font-tech tracking-wide text-sm md:text-base lg:text-lg mb-1"
        [class.text-white]="tile.isActive"
        [class.text-white/35]="!tile.isActive">
        {{ tile.machineCode }}
      </div>

      @if (tile.isActive) {
      <div class="text-white font-tech text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-none tracking-tight mt-1">
        {{ tile.lbsPerHour }}
      </div>
      <div class="text-white/45 text-[10px] tracking-[0.18em] uppercase mt-1">Lbs / Hora</div>
      <div class="text-white/50 text-[10px] sm:text-xs tracking-wide mt-2 flex items-center gap-2">
        @if (tile.gauge !== null) {
        <span>{{ tile.gauge }}G</span>
        <span class="text-white/25">·</span>
        }
        <span>{{ tile.rollCount }} rollos</span>
      </div>
      } @else {
      <div class="text-white/25 font-tech text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-none tracking-tight mt-1">
        —
      </div>
      <div class="text-white/40 text-[10px] tracking-[0.18em] uppercase mt-2">{{ tile.statusLabel }}</div>
      }

      <div class="mt-2 sm:mt-3 h-[2px] bg-white/10 rounded-sm overflow-hidden">
        <div
          class="h-full bar-fill"
          [class.bg-green-500]="tile.tier === 'top'"
          [class.bg-amber-500]="tile.tier === 'low'"
          [class.bg-white/45]="tile.tier === 'normal'"
          [style.width.%]="tile.barPercent"></div>
      </div>
    </div>
    }
  </div>
  }

  <!-- Footer Legend -->
  <div
    class="relative z-10 mt-2 sm:mt-3 md:mt-4 flex items-center justify-between text-white/50 text-[10px] sm:text-xs md:text-sm font-light tracking-wide">
    <div class="flex items-center gap-3 sm:gap-4 md:gap-6 flex-wrap">
      <span class="inline-flex items-center gap-2">
        <span class="inline-block w-3 h-[2px] rounded-sm bg-green-500"></span>
        Sobre meta
      </span>
      <span class="inline-flex items-center gap-2">
        <span class="inline-block w-3 h-[2px] rounded-sm bg-white/45"></span>
        Normal
      </span>
      <span class="inline-flex items-center gap-2">
        <span class="inline-block w-3 h-[2px] rounded-sm bg-amber-500"></span>
        Bajo meta
      </span>
      @if (topMachine()) {
      <span class="text-white/40">
        Barra = % vs {{ topMachine()!.machineCode }} ({{ topMachine()!.lbsPerHour }} Lbs/h)
      </span>
      }
    </div>
  </div>
</div>
